if (!customElements.get('adasatee-source-brands')) {
class AdasateeSourceBrands extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const {signal} = this.controller;
    this.viewport = this.querySelector('[data-brands-viewport]');
    this.track = this.querySelector('[data-brands-track]');
    this.slides ??= Array.from(this.querySelectorAll('[data-brand-handle]'));
    if (!this.viewport || !this.track || !this.slides.length) return;
    this.start ??= 0;
    this.suppressClick = false;
    this.previous = this.querySelector('[data-brands-previous]');
    this.next = this.querySelector('[data-brands-next]');
    this.setAttribute('data-enhanced', '');
    this.previous.addEventListener('click', () => this.move(-1), {signal});
    this.next.addEventListener('click', () => this.move(1), {signal});
    this.viewport.addEventListener('keydown', event => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const rtl = getComputedStyle(this).direction === 'rtl';
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.move((event.key === 'ArrowRight') !== rtl ? 1 : -1);
      }
    }, {signal});
    this.viewport.addEventListener('pointerdown', event => {
      if (!event.isPrimary || event.button !== 0) return;
      this.animation?.cancel();
      this.gesture = {id: event.pointerId, x: event.clientX, y: event.clientY, delta: 0, dragged: false};
      this.suppressClick = false;
    }, {signal});
    this.viewport.addEventListener('pointermove', event => {
      const gesture = this.gesture;
      if (!gesture || event.pointerId !== gesture.id) return;
      const delta = event.clientX - gesture.x;
      if (!gesture.dragged && Math.abs(event.clientY - gesture.y) > Math.abs(delta)) { this.gesture = null; return; }
      if (!gesture.dragged && Math.abs(delta) < 5) return;
      gesture.dragged = true;
      gesture.delta = delta;
      this.viewport.setPointerCapture(event.pointerId);
      this.setAttribute('data-dragging', '');
      this.track.style.transform = `translateX(${Math.max(-this.viewport.clientWidth / 3, Math.min(this.viewport.clientWidth / 3, delta))}px)`;
      event.preventDefault();
    }, {signal});
    const finish = event => {
      const gesture = this.gesture;
      if (!gesture || event.pointerId !== gesture.id) return;
      this.gesture = null;
      this.track.style.transform = '';
      this.removeAttribute('data-dragging');
      if (this.viewport.hasPointerCapture(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
      if (!gesture.dragged) return;
      this.suppressClick = true;
      if (event.type === 'pointerup') {
        const rtl = getComputedStyle(this).direction === 'rtl';
        this.move((gesture.delta < 0) !== rtl ? 1 : -1);
      }
    };
    for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) this.viewport.addEventListener(type, finish, {signal});
    this.viewport.addEventListener('click', event => {
      if (this.suppressClick && event.detail !== 0) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.suppressClick = false;
      }
    }, {capture: true, signal});
    this.viewport.addEventListener('dragstart', event => event.preventDefault(), {signal});
    this.resizeObserver = new ResizeObserver(() => this.layout());
    this.resizeObserver.observe(this.viewport);
    this.layout();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    this.animation?.cancel();
  }

  layout() {
    const focused = document.activeElement;
    const focusedIndex = this.slides.findIndex(slide => slide.contains(focused));
    const columns = Number(getComputedStyle(this).getPropertyValue('--source-brand-columns')) || 2;
    if (this.columns !== columns) {
      this.columns = columns;
      this.start = focusedIndex < 0 ? 0 : Math.floor(focusedIndex / columns) * columns;
    }
    this.pages = Math.ceil(this.slides.length / this.columns);
    this.previous.hidden = this.next.hidden = this.pages < 2;
    this.dataset.columns = this.columns;
    this.dataset.start = this.start;
    this.slides.forEach((slide, index) => {
      const position = (index - this.start + this.slides.length) % this.slides.length;
      slide.style.order = position;
      slide.inert = position >= this.columns;
    });
    const ordered = [...this.slides].sort((a, b) => Number(a.style.order) - Number(b.style.order));
    ordered.forEach((slide, index) => {
      if (this.track.children[index] !== slide) this.track.insertBefore(slide, this.track.children[index] || null);
    });
    if (focusedIndex >= 0) {
      if (this.slides[focusedIndex].inert) this.viewport.focus({preventScroll: true});
      else if (document.activeElement !== focused) focused.focus({preventScroll: true});
    }
  }

  move(direction) {
    if (this.pages < 2) return;
    const page = Math.floor(this.start / this.columns);
    this.start = ((page + direction + this.pages) % this.pages) * this.columns;
    this.layout();
    this.animation?.cancel();
    if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const rtl = getComputedStyle(this).direction === 'rtl';
      const offset = direction * (rtl ? -1 : 1) * this.viewport.clientWidth;
      this.animation = this.track.animate([{transform:`translateX(${offset}px)`}, {transform:'translateX(0)'}], {duration:300,easing:'ease-out'});
    }
  }
}

customElements.define('adasatee-source-brands', AdasateeSourceBrands);
}
